import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getFeatureFlags,
  setFeatureFlag,
  getFlagAudit,
  triggerMarketRecompute,
  getRecomputeJob,
  getSchedulerStatus,
} from "../api/adminApi";
import {
  Section,
  StatusBadge,
  ShortId,
  fmtDate,
} from "../components/admin/AdminUI";

// --------------------------------------------------------------------------- //
// Friendly descriptions used in the toggle confirm modal + flag row.          //
// Keep aligned with backend Registry (services/settings/runtime_flags.py).    //
// --------------------------------------------------------------------------- //
const FLAG_FRIENDLY = {
  MARKET_KILL_SWITCH_ENABLED: {
    label: "Market Kill-Switch",
    de: "Unterdrückt Market Signals und Marktpreis-Schätzungen bei niedriger Datenqualität (weak_cohort, brand_only, family_only, low_confidence, performance_mismatch). Empfohlen: AN.",
    warnOnEnable: false,
  },
  IMMO_FEED_MATERIALIZED_ENABLED: {
    label: "Immo Feed (Materialized)",
    de: "Liest Immo-Feed aus vorberechneter Cohort-Collection statt Live-Aggregation. Verbessert p50 Latenz drastisch. ⚠️ Voraussetzung: Cron läuft (alle 30min). KEINE Aktivierung ohne P.1+P.2 Stabilisierung.",
    warnOnEnable: true,
  },
  AUTO_FEED_MATERIALIZED_ENABLED: {
    label: "Auto Feed (Materialized)",
    de: "Liest Auto-Feed aus vorberechneter Cohort-Collection. Analog zu Immo. ⚠️ Voraussetzung: P.1+P.2 stabilisiert.",
    warnOnEnable: true,
  },
};

const MARKER_CATALOG = [
  "amg_full", "rs_audi", "m_full", "gti", "clubsport",
  "porsche_turbo_s", "porsche_turbo", "porsche_gt3", "porsche_gts",
  "n_hyundai", "gr_toyota", "quadrifoglio", "vz_cupra",
  "r_vw", "s_audi", "type_r_honda", "sti_subaru",
  "nismo_nissan", "alpina", "f_lexus",
];

function relativeTime(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const s = Math.round(ms / 1000);
  if (s < 60) return `vor ${s}s`;
  if (s < 3600) return `vor ${Math.round(s / 60)}min`;
  if (s < 86400) return `vor ${Math.round(s / 3600)}h`;
  return `vor ${Math.round(s / 86400)}d`;
}

function FlagRow({ flagName, meta, onToggle, isPending }) {
  const friendly = FLAG_FRIENDLY[flagName] || {
    label: flagName, de: "(keine Beschreibung registriert)", warnOnEnable: false,
  };
  const sourceLabel = {
    db: { txt: "DB-Override (live)", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
    env: { txt: ".env Fallback", cls: "bg-sky-500/20 text-sky-300 border-sky-500/40" },
    default: { txt: "Registry-Default", cls: "bg-zinc-700/30 text-zinc-400 border-zinc-700" },
  }[meta.source] || { txt: meta.source, cls: "bg-zinc-700/30 text-zinc-400 border-zinc-700" };
  return (
    <div
      data-testid={`flag-row-${flagName}`}
      className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4 space-y-3"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-zinc-100">{friendly.label}</span>
            <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${sourceLabel.cls}`}>
              {sourceLabel.txt}
            </span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mt-0.5">
            {flagName}
          </div>
        </div>
        <button
          type="button"
          disabled={isPending}
          onClick={() => onToggle(flagName, !meta.value)}
          data-testid={`flag-toggle-${flagName}`}
          aria-label={`Toggle ${flagName}`}
          className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
            meta.value
              ? "bg-emerald-500/80 border-emerald-400"
              : "bg-zinc-800 border-zinc-700"
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-zinc-100 shadow ring-0 transition-transform ${
              meta.value ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>
      <p className="text-xs text-zinc-400 leading-relaxed">{friendly.de}</p>
      <div className="flex items-center gap-4 text-[11px] text-zinc-500 pt-2 border-t border-zinc-800">
        <span>
          Default:{" "}
          <span className="font-mono text-zinc-400">
            {String(meta.default)}
          </span>
        </span>
        <span>•</span>
        <span data-testid={`flag-${flagName}-updated`}>
          {meta.updated_at
            ? `geändert ${relativeTime(meta.updated_at)} von ${meta.updated_by || "—"}`
            : "noch nie geändert"}
        </span>
      </div>
    </div>
  );
}

function ConfirmToggleModal({ open, flagName, nextValue, onCancel, onConfirm, isPending }) {
  const friendly = FLAG_FRIENDLY[flagName] || { label: flagName || "", de: "" };
  const needsHardConfirm =
    nextValue === true && (friendly.warnOnEnable || false);
  const [confirmText, setConfirmText] = useState("");
  // Reset the hard-confirm text whenever the modal opens for a new flag.
  React.useEffect(() => {
    if (open) setConfirmText("");
  }, [open, flagName]);
  if (!open) return null;
  const requiredText = needsHardConfirm ? "AKTIVIEREN" : null;
  const ready = !needsHardConfirm || confirmText === requiredText;
  return (
    <div
      data-testid="flag-confirm-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6 space-y-4 shadow-2xl">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
            confirm flag change
          </div>
          <h3 className="text-lg text-zinc-100 mt-1">{friendly.label}</h3>
          <div className="font-mono text-[11px] text-zinc-500 mt-1">{flagName}</div>
        </div>
        <div className="rounded-md bg-zinc-900/60 border border-zinc-800 p-3 text-sm">
          <span className="text-zinc-300">Wert wird gesetzt auf </span>
          <span
            className={`font-mono px-2 py-0.5 rounded ${
              nextValue
                ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                : "bg-zinc-800 text-zinc-300 border border-zinc-700"
            }`}
          >
            {String(nextValue)}
          </span>
        </div>
        <p className="text-xs text-zinc-400 leading-relaxed">{friendly.de}</p>
        {needsHardConfirm && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
            <div className="text-xs text-amber-200">
              ⚠️ Dieser Flag verändert die Read-Path-Architektur. Sicher aktivieren?
            </div>
            <input
              type="text"
              data-testid="flag-confirm-hard-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder='Tippe "AKTIVIEREN" zur Bestätigung'
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-100 placeholder-zinc-600"
            />
          </div>
        )}
        <div className="flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            data-testid="flag-confirm-cancel"
            className="px-3 py-1.5 rounded text-sm text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => onConfirm(flagName, nextValue)}
            disabled={isPending || !ready}
            data-testid="flag-confirm-submit"
            className="px-3 py-1.5 rounded text-sm font-medium bg-emerald-500/80 hover:bg-emerald-500 text-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Speichern…" : "Bestätigen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FeatureFlagsCard() {
  const queryClient = useQueryClient();
  const flagsQ = useQuery({
    queryKey: ["admin", "settings", "flags"],
    queryFn: getFeatureFlags,
    staleTime: 10_000,
  });
  const [modal, setModal] = useState({ open: false, flag: null, value: null });

  const toggle = useMutation({
    mutationFn: ({ flag, value }) => setFeatureFlag(flag, value),
    onSuccess: (resp) => {
      toast.success(
        `Flag ${resp.flag} = ${resp.value} • audit: ${resp.audit_event_id?.slice(0, 8)}…`,
        { duration: 6000 },
      );
      setModal({ open: false, flag: null, value: null });
      queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });
    },
    onError: (err) => {
      toast.error(`Toggle fehlgeschlagen: ${err?.message || err}`);
    },
  });

  const startToggle = (flag, nextValue) => {
    setModal({ open: true, flag, value: nextValue });
  };
  const confirmToggle = (flag, value) => {
    toggle.mutate({ flag, value });
  };

  if (flagsQ.isLoading) return <div className="text-zinc-500 text-sm">Lädt Feature Flags…</div>;
  if (flagsQ.isError) {
    return (
      <div data-testid="flags-error" className="text-rose-400 text-sm">
        Fehler beim Laden: {String(flagsQ.error?.message || flagsQ.error)}
      </div>
    );
  }

  const flags = flagsQ.data?.flags || {};
  const entries = Object.entries(flags);
  return (
    <Section
      title="Feature Flags"
      testid="ops-card-flags"
      action={
        <button
          type="button"
          onClick={() => flagsQ.refetch()}
          data-testid="flags-refresh"
          className="text-xs text-zinc-500 hover:text-zinc-200"
        >
          ↻ Refresh
        </button>
      }
    >
      <div className="grid gap-3">
        {entries.map(([flagName, meta]) => (
          <FlagRow
            key={flagName}
            flagName={flagName}
            meta={meta}
            isPending={toggle.isPending}
            onToggle={startToggle}
          />
        ))}
        {entries.length === 0 && (
          <div className="text-xs text-zinc-500">(keine Flags registriert)</div>
        )}
      </div>
      <ConfirmToggleModal
        open={modal.open}
        flagName={modal.flag}
        nextValue={modal.value}
        isPending={toggle.isPending}
        onCancel={() => setModal({ open: false, flag: null, value: null })}
        onConfirm={confirmToggle}
      />
    </Section>
  );
}

// --------------------------------------------------------------------------- //
// Card 2 — Market Controls                                                    //
// --------------------------------------------------------------------------- //
function RecomputeJobBadge({ job }) {
  if (!job) return null;
  return (
    <div
      data-testid={`recompute-job-${job.job_id}`}
      className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-3 text-xs space-y-1"
    >
      <div className="flex items-center gap-2">
        <ShortId value={job.job_id} length={10} />
        <StatusBadge status={job.status || "running"} />
        <span className="text-zinc-500">
          {job.scope?.filter_type || "broad"} •{" "}
          {job.scope?.estimated_count != null
            ? `${job.scope.estimated_count} listings`
            : "broad slice"}
        </span>
      </div>
      <div className="text-zinc-500">
        gestartet {relativeTime(job.started_at)} • poll:{" "}
        <code className="text-zinc-400">{job.poll_url}</code>
      </div>
      {job.summary && (
        <div className="text-zinc-400 pt-1 border-t border-zinc-800 mt-1">
          processed={job.summary.processed ?? "—"} • errors=
          {job.summary.errors ?? "—"} • strict=
          {job.summary.strict_signal_emitted ?? "—"} • soft=
          {job.summary.soft_signal_emitted ?? "—"} • {job.summary.duration_ms ?? "—"}ms
        </div>
      )}
    </div>
  );
}

function MarketControlsCard() {
  const [confirm, setConfirm] = useState(null); // {label, payload, scopeText}
  const [recentJobs, setRecentJobs] = useState([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedMarkers, setSelectedMarkers] = useState([]);
  const [listingIdsText, setListingIdsText] = useState("");

  const recompute = useMutation({
    mutationFn: triggerMarketRecompute,
    onSuccess: (job) => {
      toast.success(
        `Recompute gestartet • ${job.scope?.filter_type} • ${
          job.scope?.estimated_count ?? "broad"
        } listings`,
      );
      setRecentJobs((prev) => [job, ...prev].slice(0, 5));
      setConfirm(null);
    },
    onError: (err) => {
      const status = err?.response?.status;
      const code = err?.response?.data?.error?.code;
      toast.error(`Recompute fehlgeschlagen (${status || "?"}): ${code || err?.message}`);
    },
  });

  // Poll the most recent running job every 5s.
  const liveJob = recentJobs[0];
  const pollQ = useQuery({
    queryKey: ["admin", "market", "recompute-job", liveJob?.job_id],
    queryFn: () => getRecomputeJob(liveJob.job_id),
    enabled: Boolean(liveJob && liveJob.status !== "completed" && liveJob.status !== "failed"),
    refetchInterval: 3000,
  });
  React.useEffect(() => {
    if (pollQ.data && liveJob && pollQ.data.status !== liveJob.status) {
      setRecentJobs((prev) =>
        prev.map((j) => (j.job_id === pollQ.data.job_id ? { ...j, ...pollQ.data } : j)),
      );
    }
  }, [pollQ.data, liveJob]);

  const startRecompute = (label, payload, scopeText) => {
    setConfirm({ label, payload, scopeText });
  };
  const confirmAndSend = () => {
    if (!confirm) return;
    recompute.mutate(confirm.payload);
  };

  const submitMarkerSubset = () => {
    const payload = {
      performance_markers_only: true,
      ...(selectedMarkers.length > 0 ? { marker_types: selectedMarkers } : {}),
    };
    startRecompute(
      "Performance-Marker (Auswahl)",
      payload,
      `Performance Cars${
        selectedMarkers.length > 0 ? ` (${selectedMarkers.length} Marker)` : " (alle Marker)"
      }`,
    );
  };
  const submitListingIds = () => {
    const ulids = listingIdsText
      .split(/\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (ulids.length === 0) {
      toast.error("Bitte mindestens eine ULID eingeben (eine pro Zeile)");
      return;
    }
    startRecompute(
      `${ulids.length} Listings (gezielt)`,
      { listing_ids: ulids },
      `${ulids.length} Listings (gezielter Audit)`,
    );
  };

  return (
    <Section
      title="Market Controls"
      testid="ops-card-market"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button
          type="button"
          data-testid="recompute-auto-broad"
          onClick={() =>
            startRecompute(
              "Auto (broad)",
              { vertical: "auto" },
              "ALLE aktiven Auto-Listings (~33.172, ~8-15 min)",
            )
          }
          className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4 text-left hover:bg-zinc-900/60 transition"
        >
          <div className="text-sm font-medium text-zinc-100">Recompute Auto</div>
          <div className="text-xs text-zinc-500 mt-1">
            vertical=auto • broad slice
          </div>
        </button>
        <button
          type="button"
          data-testid="recompute-immo-broad"
          onClick={() =>
            startRecompute(
              "Immo (broad)",
              { vertical: "real_estate" },
              "ALLE aktiven Immo-Listings (~51.085, ~15-30 min)",
            )
          }
          className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4 text-left hover:bg-zinc-900/60 transition"
        >
          <div className="text-sm font-medium text-zinc-100">Recompute Immo</div>
          <div className="text-xs text-zinc-500 mt-1">
            vertical=real_estate • broad slice
          </div>
        </button>
        <button
          type="button"
          data-testid="recompute-perf-cars"
          onClick={() =>
            startRecompute(
              "Performance Cars",
              { performance_markers_only: true },
              "Performance-Cars (~660–1.650 Listings, 30s – 2min) • Sprint Q M.1 Empfohlen",
            )
          }
          className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-left hover:bg-emerald-500/15 transition"
        >
          <div className="text-sm font-medium text-emerald-200">
            ⭐ Recompute Performance Cars
          </div>
          <div className="text-xs text-emerald-300/70 mt-1">
            performance_markers_only=true • empfohlen nach M.1-Aktivierung
          </div>
        </button>
      </div>

      <button
        type="button"
        onClick={() => setAdvancedOpen((v) => !v)}
        data-testid="recompute-advanced-toggle"
        className="text-xs text-zinc-500 hover:text-zinc-200"
      >
        {advancedOpen ? "▾" : "▸"} Advanced (Marker-Auswahl / Listing-IDs)
      </button>

      {advancedOpen && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">
              Marker-Auswahl
            </div>
            <div className="flex flex-wrap gap-1.5">
              {MARKER_CATALOG.map((m) => {
                const sel = selectedMarkers.includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    data-testid={`marker-chip-${m}`}
                    onClick={() =>
                      setSelectedMarkers((prev) =>
                        sel ? prev.filter((x) => x !== m) : [...prev, m],
                      )
                    }
                    className={`px-2 py-0.5 rounded text-[11px] border ${
                      sel
                        ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/40"
                        : "bg-zinc-900 text-zinc-400 border-zinc-700 hover:border-zinc-600"
                    }`}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={submitMarkerSubset}
              data-testid="recompute-marker-submit"
              className="mt-3 px-3 py-1.5 rounded text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
            >
              Recompute Selected Markers ({selectedMarkers.length || "alle"})
            </button>
          </div>
          <div className="border-t border-zinc-800 pt-4">
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">
              Listing IDs (gezielter Audit)
            </div>
            <textarea
              data-testid="recompute-listing-ids-input"
              value={listingIdsText}
              onChange={(e) => setListingIdsText(e.target.value)}
              rows={4}
              placeholder="Eine ULID pro Zeile, z.B. 01KVF4QG5VWGR3BSQCQF7TYE0W"
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs font-mono text-zinc-100 placeholder-zinc-600"
            />
            <button
              type="button"
              onClick={submitListingIds}
              data-testid="recompute-listing-ids-submit"
              className="mt-2 px-3 py-1.5 rounded text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
            >
              Recompute Listings
            </button>
          </div>
        </div>
      )}

      {recentJobs.length > 0 && (
        <div className="space-y-2 pt-2">
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">
            Letzte Jobs ({recentJobs.length})
          </div>
          {recentJobs.map((j) => (
            <RecomputeJobBadge key={j.job_id} job={j} />
          ))}
        </div>
      )}

      {confirm && (
        <div
          data-testid="recompute-confirm-modal"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        >
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6 space-y-4 shadow-2xl">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400">
                production write
              </div>
              <h3 className="text-lg text-zinc-100 mt-1">Recompute starten</h3>
            </div>
            <div className="rounded-md bg-zinc-900/60 border border-zinc-800 p-3 text-sm space-y-1">
              <div className="text-zinc-300">{confirm.label}</div>
              <div className="text-xs text-zinc-500">Scope: {confirm.scopeText}</div>
              <pre className="font-mono text-[10px] text-zinc-500 mt-1 overflow-x-auto">
                {JSON.stringify(confirm.payload, null, 2)}
              </pre>
            </div>
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
              ⚠️ Diese Operation schreibt in Production-DB (auto_analysis /
              real_estate_analysis Updates). Rollback ist via erneuten Recompute
              mit Flag=false möglich (Reset-Pfad).
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                disabled={recompute.isPending}
                data-testid="recompute-confirm-cancel"
                className="px-3 py-1.5 rounded text-sm text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={confirmAndSend}
                disabled={recompute.isPending}
                data-testid="recompute-confirm-submit"
                className="px-3 py-1.5 rounded text-sm font-medium bg-amber-500/80 hover:bg-amber-500 text-zinc-950 disabled:opacity-50"
              >
                {recompute.isPending ? "Starte…" : "Bestätigen & Recompute"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

// --------------------------------------------------------------------------- //
// Card 3 — Scheduler + Audit                                                  //
// --------------------------------------------------------------------------- //
const RELEVANT_JOBS = new Set([
  "immo_feed_rebuild_30m",
  "auto_feed_rebuild_30m",
]);

// Prod-Robustness Fix (2026-07): humanise the `last_summary` dict emitted
// by workers so a non-programmer admin can read the outcome without JSON.
function formatSummaryInline(summary) {
  if (!summary || typeof summary !== "object") return null;
  const bits = [];
  if (summary.status && summary.status !== "completed") bits.push(String(summary.status));
  if (typeof summary.rebuilt_count === "number") bits.push(`rebuilt ${summary.rebuilt_count.toLocaleString("de-DE")}`);
  if (typeof summary.source_count === "number") bits.push(`source ${summary.source_count.toLocaleString("de-DE")}`);
  if (typeof summary.prior_count === "number") bits.push(`prior ${summary.prior_count.toLocaleString("de-DE")}`);
  if (typeof summary.processed === "number") bits.push(`processed ${summary.processed.toLocaleString("de-DE")}`);
  if (typeof summary.total_missing === "number") bits.push(`missing ${summary.total_missing.toLocaleString("de-DE")}`);
  if (summary.swap_method) bits.push(`swap=${summary.swap_method}`);
  return bits.length > 0 ? bits.join(" • ") : null;
}

function SchedulerCard() {
  const [expandedJob, setExpandedJob] = useState(null);
  const schedQ = useQuery({
    queryKey: ["admin", "scheduler", "status"],
    queryFn: getSchedulerStatus,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  if (schedQ.isLoading) return <div className="text-zinc-500 text-sm">Lädt Scheduler-Status…</div>;
  if (schedQ.isError)
    return (
      <div data-testid="scheduler-error" className="text-rose-400 text-sm">
        Fehler: {String(schedQ.error?.message || schedQ.error)}
      </div>
    );
  const data = schedQ.data || {};
  const jobs = data.jobs || [];

  return (
    <Section
      title="Scheduler"
      testid="ops-card-scheduler"
      action={
        <div className="text-[11px] text-zinc-500">
          {data.enabled ? "✓ enabled" : "✗ disabled"} •{" "}
          {data.running ? "running" : "idle"} • tz={data.timezone || "?"} • Jobs={jobs.length}
        </div>
      }
    >
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-zinc-500 border-b border-zinc-800">
            <tr>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Job</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Schedule</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Last Run</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Status</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Detail</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Next</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => {
              const highlight = RELEVANT_JOBS.has(j.job_id);
              const isFailed = j.last_status === "failed" || j.last_status === "retry_scheduled";
              const isExpanded = expandedJob === j.job_id;
              const canExpand = Boolean(j.last_error) || Boolean(j.last_summary);
              const summaryInline = formatSummaryInline(j.last_summary);
              return (
                <React.Fragment key={j.job_id}>
                  <tr
                    data-testid={`scheduler-row-${j.job_id}`}
                    className={`border-b border-zinc-900 ${
                      highlight ? "bg-emerald-500/5" : ""
                    } ${isFailed ? "bg-rose-500/5" : ""}`}
                  >
                    <td className="px-3 py-2 font-mono text-zinc-200">
                      {j.job_id}
                      {highlight && (
                        <span className="ml-2 text-[10px] text-emerald-400">★</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-zinc-400">{j.schedule || j.interval_minutes ? `${j.interval_minutes ?? j.schedule}m` : "—"}</td>
                    <td className="px-3 py-2 text-zinc-400">
                      {j.last_run_at ? relativeTime(j.last_run_at) : "—"}
                      {typeof j.last_duration_ms === "number" && j.last_duration_ms > 0 && (
                        <span className="ml-2 text-[10px] text-zinc-600">({j.last_duration_ms}ms)</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {j.last_status ? <StatusBadge status={j.last_status} /> : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-zinc-400">
                      {canExpand ? (
                        <button
                          type="button"
                          onClick={() => setExpandedJob(isExpanded ? null : j.job_id)}
                          data-testid={`scheduler-detail-toggle-${j.job_id}`}
                          className={`text-[11px] underline underline-offset-2 ${
                            isFailed ? "text-rose-400 hover:text-rose-300" : "text-zinc-400 hover:text-zinc-200"
                          }`}
                        >
                          {isExpanded ? "verbergen" : (isFailed ? "Fehler anzeigen" : "Details")}
                        </button>
                      ) : summaryInline ? (
                        <span className="text-[11px] text-zinc-500 truncate max-w-[240px] inline-block align-bottom" title={summaryInline}>
                          {summaryInline}
                        </span>
                      ) : (
                        <span className="text-zinc-700">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-zinc-400">
                      {j.next_run_at ? fmtDate(j.next_run_at) : "—"}
                    </td>
                  </tr>
                  {isExpanded && canExpand && (
                    <tr
                      data-testid={`scheduler-detail-${j.job_id}`}
                      className="bg-zinc-950/80 border-b border-zinc-900"
                    >
                      <td colSpan={6} className="px-3 py-3">
                        <div className="space-y-2">
                          {summaryInline && (
                            <div className="text-[11px] text-zinc-400">
                              <span className="text-zinc-500 uppercase font-mono mr-2">Summary:</span>
                              {summaryInline}
                            </div>
                          )}
                          {j.last_error && (
                            <div>
                              <div className="text-[11px] text-rose-400 uppercase font-mono mb-1">
                                last_error
                              </div>
                              <pre
                                data-testid={`scheduler-error-detail-${j.job_id}`}
                                className="text-[11px] text-rose-300 whitespace-pre-wrap font-mono bg-rose-950/30 border border-rose-900/50 rounded p-2 max-h-64 overflow-auto"
                              >
                                {String(j.last_error)}
                              </pre>
                            </div>
                          )}
                          {j.last_summary && typeof j.last_summary === "object" && Object.keys(j.last_summary).length > 0 && (
                            <details className="text-[11px] text-zinc-500">
                              <summary className="cursor-pointer text-zinc-400 hover:text-zinc-200">
                                raw summary JSON
                              </summary>
                              <pre className="mt-1 whitespace-pre-wrap font-mono bg-zinc-900/50 border border-zinc-800 rounded p-2 max-h-48 overflow-auto">
                                {JSON.stringify(j.last_summary, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {jobs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-3 text-center text-zinc-500">
                  (keine Jobs)
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function FlagAuditCard() {
  const [filterFlag, setFilterFlag] = useState("");
  const auditQ = useQuery({
    queryKey: ["admin", "settings", "flags", "audit", filterFlag],
    queryFn: () =>
      getFlagAudit({ limit: 20, ...(filterFlag ? { flag: filterFlag } : {}) }),
    refetchInterval: 30_000,
  });
  if (auditQ.isLoading) return <div className="text-zinc-500 text-sm">Lädt Audit-Log…</div>;
  if (auditQ.isError) {
    return (
      <div data-testid="audit-error" className="text-rose-400 text-sm">
        Fehler: {String(auditQ.error?.message || auditQ.error)}
      </div>
    );
  }
  const items = auditQ.data?.items || [];
  return (
    <Section
      title="Flag Audit-Log"
      testid="ops-card-audit"
      action={
        <select
          value={filterFlag}
          onChange={(e) => setFilterFlag(e.target.value)}
          data-testid="audit-filter-select"
          className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-200"
        >
          <option value="">Alle Flags</option>
          {Object.keys(FLAG_FRIENDLY).map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      }
    >
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-zinc-500 border-b border-zinc-800">
            <tr>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Zeitpunkt</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Flag</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Wechsel</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Actor</th>
            </tr>
          </thead>
          <tbody data-testid="audit-table-body">
            {items.map((row) => (
              <tr key={row.ulid} className="border-b border-zinc-900">
                <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">
                  {fmtDate(row.created_at)}
                </td>
                <td className="px-3 py-2 font-mono text-zinc-200">{row.flag_name}</td>
                <td className="px-3 py-2 font-mono text-zinc-400">
                  <span className="text-zinc-500">{String(row.old_value)}</span>
                  {" → "}
                  <span className="text-emerald-300">{String(row.new_value)}</span>
                </td>
                <td className="px-3 py-2 text-zinc-400">{row.actor_email || "—"}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-3 text-center text-zinc-500">
                  (keine Audit-Events)
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// --------------------------------------------------------------------------- //
// Page shell                                                                  //
// --------------------------------------------------------------------------- //
export default function AdminOperationsPage() {
  return (
    <div
      data-testid="admin-operations-page"
      className="space-y-8"
    >
      <header>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
          /api/admin/settings · /api/admin/market/recompute
        </div>
        <h1 className="text-2xl text-zinc-100 mt-1">Operations</h1>
        <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
          System Controls — Feature Flags umschalten, Market-Engine Recomputes
          triggern, Scheduler-Jobs überwachen und Audit-Trail einsehen. Alle
          Aktionen werden in <code>admin_events</code> protokolliert.
        </p>
      </header>
      <FeatureFlagsCard />
      <MarketControlsCard />
      <SchedulerCard />
      <FlagAuditCard />
    </div>
  );
}
